import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Image as ImageIcon, Upload, Loader2, Cloud, Database } from "lucide-react";
import { resolveImageUrl } from "@/lib/imageUrl";

export default function AdminGallery() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadConfig, setUploadConfig] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", before_image_url: "", after_image_url: "", tags: "", is_before_after: true
  });
  const beforeFileRef = useRef(null);
  const afterFileRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchItems = useCallback(() => {
    axios.get(`${API}/gallery`).then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const fetchUploadConfig = useCallback(() => {
    axios.get(`${API}/admin/upload-config`, { headers }).then(r => setUploadConfig(r.data)).catch(() => {});
  }, [headers]);

  useEffect(() => { fetchItems(); fetchUploadConfig(); }, [fetchItems, fetchUploadConfig]);

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/upload-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        timeout: 60000
      });
      // Cloudinary returns full CDN URL — use as-is
      if (res.data.url && res.data.url.startsWith("http")) {
        return res.data.url;
      }
      // MongoDB — store RELATIVE path so it works on ANY domain
      return `/api/images/${res.data.id}`;
    } catch (err) {
      console.error("Upload error:", err);
      throw err;
    }
  };

  const handleFileSelect = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10MB");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm(p => ({ ...p, [field]: url }));
      toast.success("Image uploaded!");
    } catch (err) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", description: "", before_image_url: "", after_image_url: "", tags: "", is_before_after: true });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title,
      description: item.description,
      before_image_url: item.before_image_url || "",
      after_image_url: item.after_image_url || "",
      tags: (item.tags || []).join(", "),
      is_before_after: item.is_before_after !== false
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    const payload = {
      title: form.title,
      description: form.description,
      before_image_url: form.before_image_url,
      after_image_url: form.after_image_url,
      tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
      is_before_after: form.is_before_after
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/gallery/${editing.id}`, payload, { headers });
        toast.success("Gallery item updated");
      } else {
        await axios.post(`${API}/admin/gallery`, payload, { headers });
        toast.success("Gallery item created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/admin/gallery/${deleteId}`, { headers });
      toast.success("Gallery item deleted");
      setDeleteId(null);
      fetchItems();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Gallery</h1>
          <p className="text-sm text-gray-500">Manage your before/after project photos. Upload images directly or paste URLs.</p>
        </div>
        <div className="flex items-center gap-3">
          {uploadConfig && (
            <Badge variant="outline" className={`gap-1.5 text-xs ${uploadConfig.cloudinary_enabled ? 'border-green-300 text-green-700 bg-green-50' : 'border-gray-300 text-gray-600 bg-gray-50'}`}>
              {uploadConfig.cloudinary_enabled ? <Cloud className="w-3 h-3" /> : <Database className="w-3 h-3" />}
              {uploadConfig.cloudinary_enabled ? 'Cloudinary CDN' : 'Local Storage'}
            </Badge>
          )}
          <Button onClick={openCreate} className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] gap-2" data-testid="admin-gallery-upload-button">
            <Plus className="w-4 h-4" /> Add Project
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 mb-3">No gallery items yet</p>
            <Button onClick={openCreate} variant="outline" className="gap-2">
              <Plus className="w-4 h-4" /> Add Your First Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <Card key={item.id} className="overflow-hidden border border-gray-100 hover:shadow-md transition-shadow" data-testid="admin-gallery-item-card">
              <div className="relative aspect-[16/10]">
                <img
                  src={resolveImageUrl(item.after_image_url || item.before_image_url, "https://placehold.co/400x250?text=No+Image")}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 flex gap-1">
                  <Button size="icon" variant="secondary" className="h-8 w-8 bg-white/90 hover:bg-white" onClick={() => openEdit(item)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="icon" variant="secondary" className="h-8 w-8 bg-white/90 hover:bg-red-50" onClick={() => setDeleteId(item.id)}>
                    <Trash2 className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex gap-1 mb-2">
                  {item.tags?.map(tag => <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>)}
                </div>
                <h3 className="font-semibold text-sm text-[#0D0D0D]">{item.title}</h3>
                <p className="text-xs text-gray-500 line-clamp-2">{item.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Gallery Item" : "Add Gallery Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} placeholder="Project name" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Brief description" rows={2} />
            </div>

            {/* Before Image */}
            <div>
              <Label>Before Image</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.before_image_url}
                  onChange={e => setForm(p => ({...p, before_image_url: e.target.value}))}
                  placeholder="Paste URL or upload below"
                  className="flex-1"
                />
                <input type="file" ref={beforeFileRef} className="hidden" accept="image/*"
                  onChange={e => handleFileSelect(e, "before_image_url")} />
                <Button type="button" variant="outline" size="icon" onClick={() => beforeFileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
              </div>
              {form.before_image_url && (
                <img src={resolveImageUrl(form.before_image_url)} alt="Before preview" className="mt-2 h-24 w-full object-cover rounded-lg border" loading="lazy" />
              )}
            </div>

            {/* After Image */}
            <div>
              <Label>After Image</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={form.after_image_url}
                  onChange={e => setForm(p => ({...p, after_image_url: e.target.value}))}
                  placeholder="Paste URL or upload below"
                  className="flex-1"
                />
                <input type="file" ref={afterFileRef} className="hidden" accept="image/*"
                  onChange={e => handleFileSelect(e, "after_image_url")} />
                <Button type="button" variant="outline" size="icon" onClick={() => afterFileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                </Button>
              </div>
              {form.after_image_url && (
                <img src={resolveImageUrl(form.after_image_url)} alt="After preview" className="mt-2 h-24 w-full object-cover rounded-lg border" loading="lazy" />
              )}
            </div>

            <div>
              <Label>Tags (comma separated)</Label>
              <Input value={form.tags} onChange={e => setForm(p => ({...p, tags: e.target.value}))} placeholder="Yard Cleanup, Debris Removal" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603]" disabled={uploading}>
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Gallery Item</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this item. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
